import * as NavLinks from './nav-links/index';
import {
  ANTRAGS_OVERVIEW_UI_MAP,
  APP_ENVIRONMENT,
  APP_VERSION,
  AppWebConfig,
  AuthenticationFacade,
  ConfigSettingsService,
  CustomErrorHandler,
  MessageBase,
  ModalServiceBase,
  NAV_LINK,
  PinInSessionService,
  PinPersistentService,
  SavePinBase,
  TelemetryService,
  TranslationInitializationService,
  VaultFacade,
  coreReducers,
  readFromJson,
  savePinServiceFactory,
} from '@ebeihilfe/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppPagesModule } from './feature/pages/app-pages.module';
import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CustomComponentModule } from './components';
// will be loaded via customer copy
import { CustomerAppModule } from './customer.app.module';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { KURZANTRAG_CONFIG } from '@ebeihilfe/einreichung-old';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MessageService } from './services';
import { ModalService, layoutFeature } from '@ebeihilfe/web-logic';
import { SentryService } from './services/sentry/sentry.service';
import { StoreDevtoolsModule, provideStoreDevtools } from '@ngrx/store-devtools';
import { TranslateCompiler, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { WebTelemetryService } from './services/telemetry/telemetry.service';
import { enableProdMode, importProvidersFrom } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideEffects } from '@ngrx/effects';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideRouterStore } from '@ngrx/router-store';
import { provideState, provideStore } from '@ngrx/store';
import { provideToastr } from 'ngx-toastr';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ApplicationsDeleteFacade, ApplicationsDetailsFacade } from '@ebeihilfe/einreichung-new/facades';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { VERSION } from '../../../../environments/version';
import { environment } from '../environments/environment';
import { getKurzantragConfig } from '../config/kurzantrag-config';
import { routes } from './app.routes';
import jsonData from '../config/antrags-overview-ui-data.json';
import localeDe from '@angular/common/locales/de';

registerLocaleData(localeDe, 'de');

export const startAsyncInitInConfigSettings =
  (
    authenticationService: AuthenticationFacade,
    configService: ConfigSettingsService,
    translate: TranslationInitializationService,
    vaultService: VaultFacade,
    sentryService: SentryService,
  ) =>
  async (): Promise<void> => {
    console.log(`Initializing web vault`);
    await configService.loadConfigProviders();
    await vaultService.init('web');
    await authenticationService.initialize();
    await translate.initializeTranslate();
    await sentryService.init();
  };

if (environment.production) {
  enableProdMode();
}

export const appConfig: ApplicationConfig = {
  providers: [
    ApplicationsDeleteFacade,
    ApplicationsDetailsFacade,
    importProvidersFrom(
      !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
      AngularSvgIconModule.forRoot(),
      AppPagesModule,
      BrowserModule,
      CustomerAppModule,
      CustomComponentModule,
      HttpClientModule,
      MatButtonModule,
      MatDialogModule,
      TranslateModule.forRoot({
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
      }),
    ),
    provideAnimations(),
    provideToastr(),
    {
      provide: APP_VERSION,
      useValue: VERSION,
    },
    {
      provide: APP_ENVIRONMENT,
      useValue: environment,
    },
    {
      provide: ANTRAGS_OVERVIEW_UI_MAP,
      useValue: readFromJson(jsonData),
    },
    provideAppInitializer(() => {
      const initializerFn = startAsyncInitInConfigSettings(
        inject(AuthenticationFacade),
        inject(ConfigSettingsService),
        inject(TranslationInitializationService),
        inject(VaultFacade),
        inject(SentryService),
      );
      return initializerFn();
    }),
    {
      provide: SavePinBase,
      useFactory: savePinServiceFactory,
      deps: [ConfigSettingsService, PinInSessionService, PinPersistentService],
    },
    {
      provide: KURZANTRAG_CONFIG,
      deps: [TranslateService],
      useFactory: getKurzantragConfig,
    },
    {
      provide: ModalServiceBase,
      useClass: ModalService,
    },
    {
      provide: MessageBase,
      useClass: MessageService,
    },
    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },
    {
      provide: TelemetryService,
      useClass: WebTelemetryService,
    },
    ...Object.values(NavLinks).map((navLink) => ({
      provide: NAV_LINK,
      useClass: navLink,
      multi: true,
    })),
    DatePipe,
    ...AppWebConfig.providers,
    provideStore(coreReducers),
    provideEffects([]),
    provideState(layoutFeature),
    provideRouterStore(),
    provideStoreDevtools(),
    provideRouter(routes, withComponentInputBinding()),
  ],
};
